export interface Segment {
    colorHex?: string | null;
    searchColorName?: string | null;
}

export interface ColorSwatch {
    name?: string | null;
    // Number of items in the pack; multi-color quadrants only render for multipacks (> 1).
    packItemsCount?: number;
    segments: Segment[];
}

// The segments to display for a swatch: the full multipack set only when it is a
// multipack (packItemsCount > 1); otherwise a single swatch from the first segment.
export const displaySegments = (swatch: ColorSwatch): Segment[] => {
    if ((swatch.packItemsCount ?? 1) > 1) {
        return swatch.segments;
    }
    return swatch.segments.slice(0, 1);
};

// Search color names that map to a dedicated visual (gradient / SVG) instead of a plain hex fill.
// Mirrors the .color-38/62/63/104 definitions in styles/06-components/_color.scss.
const SILVER_GRADIENT = 'linear-gradient(315deg, rgb(227, 231, 232) 50%, rgb(172, 184, 187) 50%)';
const GOLD_GRADIENT = 'linear-gradient(315deg, rgb(243, 235, 146) 50%, rgb(235, 221, 73) 50%)';
const COLORFUL_GRADIENT =
    'linear-gradient(315deg, rgb(176, 30, 142) 0%, rgb(25, 133, 199) 33%, rgb(243, 235, 146) 66%, rgb(255, 20, 83) 100%)';
const TRANSPARENT_SVG = 'url("/bff_resources/images/colors/transparent.svg")';

// Grey contour drawn around a swatch that contains pure white, so the white area stays
// visible against a white page. Uses the DeSy on-primary-mid token.
export const WHITE_SWATCH_BORDER = '1px solid var(--color-on-primary-mid)';
const BLACK_BORDER_COLOR = '#000000';

export interface SegmentStyle {
    background: string;
    backgroundSize?: string;
    border?: string;
}

export const isSpecial = (searchColorName?: string | null): boolean =>
    searchColorName === 'silber' || searchColorName === 'gold' || searchColorName === 'bunt' || searchColorName === 'farblos';

// Strict pure white, tolerant of shorthand (#fff) and casing. Named colors never count.
export const isWhite = (colorHex?: string | null): boolean => {
    const hex = (colorHex ?? '').trim().toLowerCase().replace('#', '');
    const full = hex.length === 3 ? hex.replace(/(.)/g, '$1$1') : hex;
    return full === 'ffffff';
};

// The segments painted as a visible colour area, mirroring renderSegments: all of them up to 4
// quadrants; for a 5+ pack the first 4 (the 4th fills the bottom-right quadrant under the small
// "+" glyph, which does not cover it), while any further segments are not rendered at all.
export const visibleSegments = (segments: Segment[]): Segment[] =>
    segments.length <= 4 ? segments : segments.slice(0, 4);

// True when a segment shown as a visible colour area is pure white → the whole swatch gets the
// grey contour. Segments beyond the visible set (the 5th onward in a "+" pack) are never painted,
// so a white one there does not trigger the border.
export const swatchContainsWhite = (segments: Segment[]): boolean =>
    visibleSegments(segments).some((s) => isWhite(s.colorHex));

export const segmentStyle = (segment: Segment): SegmentStyle => {
    switch (segment.searchColorName) {
        case 'silber':
            return {background: SILVER_GRADIENT};
        case 'gold':
            return {background: GOLD_GRADIENT};
        case 'bunt':
            return {background: COLORFUL_GRADIENT};
        case 'farblos':
            return {background: TRANSPARENT_SVG, backgroundSize: '100%', border: `1px solid ${BLACK_BORDER_COLOR}`};
        default:
            return {
                background: segment.colorHex ?? '#000000',
            };
    }
};

// Relative luminance (WCAG). Returns true for light backgrounds → use a black "+".
export const isLightColor = (colorHex?: string | null): boolean => {
    const hex = (colorHex ?? '').replace('#', '');
    if (hex.length !== 6) {
        return true;
    }
    const r = parseInt(hex.slice(0, 2), 16) / 255;
    const g = parseInt(hex.slice(2, 4), 16) / 255;
    const b = parseInt(hex.slice(4, 6), 16) / 255;
    const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
    const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
    return luminance > 0.5;
};

// The "+" glyph color for a 5+ pack: the "+" sits over the 4th color's quadrant.
export const plusGlyphColor = (fourthSegment: Segment | undefined): string => {
    if (fourthSegment && isSpecial(fourthSegment.searchColorName)) {
        // silber/gold/bunt/farblos backgrounds are light → black "+".
        return '#000000';
    }
    return isLightColor(fourthSegment?.colorHex) ? '#000000' : '#FFFFFF';
};
